import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { buildListingMapItem, buildVenueMapItem, toggleSelectableFilters } from '../../components/admin/AdminGeoMapExplorer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// All valid tab IDs — used to guard the ?tab= URL param against arbitrary values.
const VALID_TABS = new Set([
  'dashboard', 'users', 'admins', 'guests', 'notifications',
  'venue-apps', 'applications', 'approving', 'maps',
  'support', 'reports', 'disputes', 'broadcasts', 'announcements',
  'feedback', 'faq-stats', 'en-route',
  'transactions', 'referrals',
  'agreements', 'compliance', 'legal-settings',
  'experiences',
  'main-admins',
]);

const haversineDistanceKm = (lat1, lng1, lat2, lng2) => {
  const earthRadiusKm = 6371;
  const toRadians = (value) => (value * Math.PI) / 180;
  const deltaLat = toRadians(lat2 - lat1);
  const deltaLng = toRadians(lng2 - lng1);
  const a =
    Math.sin(deltaLat / 2) ** 2 +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(deltaLng / 2) ** 2;
  return earthRadiusKm * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
};

const isListingLocationUnavailable = (application) => {
  const latitude = Number(application?.listing_latitude);
  const longitude = Number(application?.listing_longitude);
  return !application?.listing_location_label || Number.isNaN(latitude) || Number.isNaN(longitude);
};

export const useAdminPanel = ({ onLogout }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // ── Tab routing ────────────────────────────────────────────────────────────
  // activeTab is derived from the URL ?tab= param so the browser's
  // back/forward buttons work and links like /admin?tab=support are shareable.
  const tabParam = searchParams.get('tab');
  const activeTab = tabParam && VALID_TABS.has(tabParam) ? tabParam : 'dashboard';

  // setActiveTab pushes a new history entry (back/forward works).
  // Pass { replace: true } as second arg for programmatic redirects that
  // shouldn't pollute history (e.g. auto-redirects after an action).
  const setActiveTab = useCallback((tab, opts = {}) => {
    const safe = VALID_TABS.has(tab) ? tab : 'dashboard';
    // Keep the URL clean: dashboard lives at /admin (no param)
    const params = safe === 'dashboard' ? {} : { tab: safe };
    setSearchParams(params, { replace: opts.replace ?? false });
  }, [setSearchParams]);

  // ── State ─────────────────────────────────────────────────────────────────
  const [applications, setApplications] = useState([]);
  const [users, setUsers] = useState([]);
  const [venues, setVenues] = useState([]);
  const [ignoredIcalEvents, setIgnoredIcalEvents] = useState([]);
  const [financeOverview, setFinanceOverview] = useState(null);
  const [financeMonth, setFinanceMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [referrals, setReferrals] = useState([]);
  const [venueApps, setVenueApps] = useState([]);
  const [selectedVenueApp, setSelectedVenueApp] = useState(null);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [selectedHost, setSelectedHost] = useState(null);
  const [guestDetail, setGuestDetail] = useState(null);
  const [hostDetail, setHostDetail] = useState(null);
  const [guestDetailLoading, setGuestDetailLoading] = useState(false);
  const [hostDetailLoading, setHostDetailLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [recentSearches, setRecentSearches] = useState(() => {
    try { return JSON.parse(localStorage.getItem('adminRecentSearches') || '[]'); }
    catch { return []; }
  });
  const [supportRequests, setSupportRequests] = useState([]);
  const [supportFilter, setSupportFilter] = useState('open');
  const [faqStats, setFaqStats] = useState({ total_events: 0, items: [] });
  const [selectedSupportRequestId, setSelectedSupportRequestId] = useState(null);
  const [selectedSupportThread, setSelectedSupportThread] = useState(null);
  const [supportDraft, setSupportDraft] = useState('');
  const [supportLoading, setSupportLoading] = useState(false);
  const [pendingApprovalCount, setPendingApprovalCount] = useState(0);
  const [unavailableListingCount, setUnavailableListingCount] = useState(0);
  const [venuesOnHoldCount, setVenuesOnHoldCount] = useState(0);
  const [supportOpenCount, setSupportOpenCount] = useState(0);
  const [reportsOpenCount, setReportsOpenCount] = useState(0);
  const [disputesOpenCount, setDisputesOpenCount] = useState(0);
  const [experiencesPendingCount, setExperiencesPendingCount] = useState(0);
  const [attentionDismissed, setAttentionDismissed] = useState(() => {
    try { return sessionStorage.getItem('adminAttentionDismissed') === '1'; }
    catch { return false; }
  });
  const [expandedTimelineId, setExpandedTimelineId] = useState(null);
  const [listingStatusFilter, setListingStatusFilter] = useState('active');
  const [globalMapFilters, setGlobalMapFilters] = useState(['all']);
  const [selectedMapId, setSelectedMapId] = useState('');
  const [radiusEnabled, setRadiusEnabled] = useState(false);
  const [floatingSupportVisible, setFloatingSupportVisible] = useState(false);
  const [floatingSupportMinimized, setFloatingSupportMinimized] = useState(false);
  const [reviewsModalVenue, setReviewsModalVenue] = useState(null);
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(() => JSON.parse(localStorage.getItem('user') || '{}'));
  const [isMobileView, setIsMobileView] = useState(() => {
    const stored = localStorage.getItem('adminMobileView');
    if (stored !== null) return stored === 'true';
    return typeof window !== 'undefined' && window.innerWidth < 768;
  });

  // ── Refs & derived ────────────────────────────────────────────────────────
  const floatingSupportBottomRef = useRef(null);
  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const user = currentUser;
  const isMainAdmin = Boolean(currentUser?.is_main_admin);
  const floatingSupportMessageCount = (selectedSupportThread?.messages || []).length;

  // ── Effects ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!floatingSupportVisible || floatingSupportMinimized) return;
    const id = window.requestAnimationFrame(() => {
      try {
        floatingSupportBottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      } catch { /* old browsers */ }
    });
    return () => window.cancelAnimationFrame(id);
  }, [floatingSupportMessageCount, floatingSupportVisible, floatingSupportMinimized, selectedSupportRequestId]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, { headers });
        localStorage.setItem('user', JSON.stringify(res.data));
        setCurrentUser(res.data);
      } catch (_err) { /* ignore */ }
    })();
  }, [headers]);

  useEffect(() => {
    if (isMobileView) {
      try { window.scrollTo({ top: 0, behavior: 'instant' }); } catch {}
    }
  }, [activeTab, isMobileView]);

  // ── Core callbacks ────────────────────────────────────────────────────────
  const logAdminDebug = useCallback((message, error) => {
    if (process.env.NODE_ENV === 'development') console.error(message, error);
  }, []);

  const refreshPendingApprovals = useCallback(async () => {
    try {
      const [venueAppsRes, hostAppsRes, supportRes, reportsRes, disputesRes, experiencesRes] = await Promise.all([
        axios.get(`${API}/admin/venue-applications`, { headers }),
        axios.get(`${API}/admin/host-applications`, { headers }),
        axios.get(`${API}/admin/support/requests`, { headers }),
        axios.get(`${API}/admin/venue-reports`, { headers }),
        axios.get(`${API}/admin/disputes`, { headers }),
        axios.get(`${API}/admin/experiences`, { headers, params: { status: 'pending' } }).catch(() => ({ data: [] })),
      ]);
      setPendingApprovalCount(venueAppsRes.data.filter((e) => e.status === 'submitted').length);
      setUnavailableListingCount(hostAppsRes.data.filter((e) => e.status === 'pending' && isListingLocationUnavailable(e)).length);
      setVenuesOnHoldCount(venueAppsRes.data.filter((e) => e.status === 'on_hold').length);
      setSupportOpenCount(
        (supportRes.data || []).filter((e) => (e.status || 'open') === 'open' && (e.reason || '').toLowerCase() !== 'feedback').length,
      );
      setReportsOpenCount((reportsRes.data || []).filter((e) => e.status === 'open').length);
      setDisputesOpenCount((disputesRes.data || []).filter((e) => e.status === 'open').length);
      setExperiencesPendingCount(Array.isArray(experiencesRes.data) ? experiencesRes.data.length : 0);
      if (activeTab === 'venue-apps') setVenueApps(venueAppsRes.data);
      if (activeTab === 'approving') { setVenueApps(venueAppsRes.data); setApplications(hostAppsRes.data); }
      if (activeTab === 'applications' || activeTab === 'maps') setApplications(hostAppsRes.data);
    } catch (err) { logAdminDebug('Failed to refresh approvals', err); }
  }, [activeTab, headers, logAdminDebug]);

  const fetchData = useCallback(async (tab) => {
    setLoading(true);
    try {
      if (tab === 'dashboard') {
        // AdminOverviewPanel fetches its own analytics on mount.
      } else if (tab === 'applications') {
        const [applicationsRes, ignoredIcalRes] = await Promise.all([
          axios.get(`${API}/admin/host-applications`, { headers }),
          axios.get(`${API}/admin/host-ical-ignored-events`, { headers }),
        ]);
        setApplications(applicationsRes.data);
        setIgnoredIcalEvents(ignoredIcalRes.data);
      } else if (tab === 'users') {
        const [usersRes, venueAppsRes] = await Promise.all([
          axios.get(`${API}/admin/users`, { headers }),
          axios.get(`${API}/admin/venue-applications`, { headers }),
        ]);
        setUsers(usersRes.data);
        setVenueApps(venueAppsRes.data);
      } else if (tab === 'venues') {
        const [venuesRes, venueAppsRes] = await Promise.all([
          axios.get(`${API}/admin/venues`, { headers }),
          axios.get(`${API}/admin/venue-applications`, { headers }),
        ]);
        setVenues(venuesRes.data);
        setVenueApps(venueAppsRes.data);
      } else if (tab === 'guests') {
        const res = await axios.get(`${API}/admin/users?role=guest`, { headers });
        setUsers(res.data);
      } else if (tab === 'transactions') {
        const financeRes = await axios.get(`${API}/admin/host-payouts/overview`, { headers, params: { month: financeMonth } });
        setFinanceOverview(financeRes.data);
      } else if (tab === 'referrals') {
        const res = await axios.get(`${API}/admin/referrals`, { headers });
        setReferrals(res.data);
      } else if (tab === 'venue-apps') {
        const [venueAppsRes, venuesRes] = await Promise.all([
          axios.get(`${API}/admin/venue-applications`, { headers }),
          axios.get(`${API}/admin/venues`, { headers }),
        ]);
        setVenueApps(venueAppsRes.data);
        setVenues(venuesRes.data);
      } else if (tab === 'approving') {
        const [venueAppsRes, referralsRes, venuesRes, hostAppsRes] = await Promise.all([
          axios.get(`${API}/admin/venue-applications`, { headers }),
          axios.get(`${API}/admin/referrals`, { headers }),
          axios.get(`${API}/admin/venues`, { headers }),
          axios.get(`${API}/admin/host-applications`, { headers }),
        ]);
        setVenueApps(venueAppsRes.data);
        setReferrals(referralsRes.data);
        setVenues(venuesRes.data);
        setApplications(hostAppsRes.data);
      } else if (tab === 'maps') {
        const [hostAppsRes, venueAppsRes] = await Promise.all([
          axios.get(`${API}/admin/host-applications`, { headers }),
          axios.get(`${API}/admin/venue-applications`, { headers }),
        ]);
        setApplications(hostAppsRes.data);
        setVenueApps(venueAppsRes.data);
      } else if (tab === 'support' || tab === 'feedback') {
        const res = await axios.get(`${API}/admin/support/requests`, { headers });
        setSupportRequests(res.data);
        if (res.data?.length) {
          setSelectedSupportRequestId((current) => current || res.data[0].id);
        }
      } else if (tab === 'faq-stats') {
        const res = await axios.get(`${API}/admin/faq/stats`, { headers });
        setFaqStats(res.data || { total_events: 0, items: [] });
      }
    } catch (err) {
      logAdminDebug('Failed to load admin data', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [financeMonth, headers, logAdminDebug]);

  useEffect(() => { fetchData(activeTab); }, [activeTab, fetchData]);
  useEffect(() => { refreshPendingApprovals(); }, [refreshPendingApprovals]);

  const fetchSupportThread = useCallback(async (requestId) => {
    if (!requestId) return;
    setSupportLoading(true);
    try {
      const res = await axios.get(`${API}/admin/support/requests/${requestId}`, { headers });
      setSelectedSupportThread(res.data);
      setSelectedSupportRequestId(requestId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load support thread');
    } finally {
      setSupportLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    if (activeTab === 'support' && selectedSupportRequestId) {
      fetchSupportThread(selectedSupportRequestId);
    }
  }, [activeTab, fetchSupportThread, selectedSupportRequestId]);

  useEffect(() => {
    const handler = (event) => {
      const incomingRequestId = event?.detail?.request_id;
      fetchData('support');
      if (selectedSupportRequestId && (!incomingRequestId || incomingRequestId === selectedSupportRequestId)) {
        fetchSupportThread(selectedSupportRequestId);
      }
    };
    window.addEventListener('allure:support-thread-changed', handler);
    return () => window.removeEventListener('allure:support-thread-changed', handler);
  }, [fetchData, fetchSupportThread, selectedSupportRequestId]);

  // ── Computed values ───────────────────────────────────────────────────────
  const searchResults = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return [];
    return users.filter((entry) => {
      const haystacks = [entry.name, entry.email, entry.customer_id].filter(Boolean).map((v) => v.toLowerCase());
      return haystacks.some((v) => v.includes(query));
    });
  }, [searchQuery, users]);

  const pendingVenueApps = useMemo(() => venueApps.filter((e) => e.status === 'submitted'), [venueApps]);
  const revisionVenueApps = useMemo(() => venueApps.filter((e) => e.status === 'revisions_requested'), [venueApps]);

  const globalMapItems = useMemo(() => {
    const listingItems = applications
      .filter((e) => ['approved', 'rejected'].includes(e.status))
      .map(buildListingMapItem)
      .filter(Boolean);
    const venueItems = venueApps.filter((e) => e.status !== 'rejected').map(buildVenueMapItem).filter(Boolean);
    const activeVenueItems = venueItems.filter((item) => item.statusKey === 'live');
    const enrichedListingItems = listingItems.map((item) => {
      if (!activeVenueItems.length) return { ...item, nearestVenueDistanceKm: null, nearestVenueTitle: '' };
      const nearestVenue = activeVenueItems.reduce((closest, venueItem) => {
        const distanceKm = haversineDistanceKm(item.latitude, item.longitude, venueItem.latitude, venueItem.longitude);
        if (!closest || distanceKm < closest.distanceKm) return { title: venueItem.title, distanceKm };
        return closest;
      }, null);
      return {
        ...item,
        nearestVenueDistanceKm: nearestVenue ? Number(nearestVenue.distanceKm.toFixed(1)) : null,
        nearestVenueTitle: nearestVenue?.title || '',
      };
    });
    if (globalMapFilters.includes('all')) return [...enrichedListingItems, ...venueItems];
    return [...enrichedListingItems, ...venueItems].filter((item) => {
      if (globalMapFilters.includes('active-listings') && item.entityType === 'listing' && item.statusKey === 'approved') return true;
      if (globalMapFilters.includes('early-listings') && item.entityType === 'listing' && item.statusKey === 'rejected') return true;
      if (globalMapFilters.includes('active-venues') && item.entityType === 'venue' && item.statusKey === 'live') return true;
      if (globalMapFilters.includes('pending-venues') && item.entityType === 'venue' && ['submitted', 'on_hold'].includes(item.statusKey)) return true;
      return false;
    });
  }, [applications, globalMapFilters, venueApps]);

  useEffect(() => {
    if (!globalMapItems.length) { setSelectedMapId(''); return; }
    if (!globalMapItems.some((item) => item.id === selectedMapId)) setSelectedMapId(globalMapItems[0].id);
  }, [globalMapItems, selectedMapId]);

  const tabCounts = useMemo(() => ({
    approving: pendingApprovalCount,
    applications: unavailableListingCount,
    'venue-apps': venuesOnHoldCount + pendingApprovalCount,
    support: supportOpenCount,
    reports: reportsOpenCount,
    disputes: disputesOpenCount,
    experiences: experiencesPendingCount,
  }), [pendingApprovalCount, unavailableListingCount, venuesOnHoldCount, supportOpenCount, reportsOpenCount, disputesOpenCount, experiencesPendingCount]);

  const attentionItems = useMemo(() => ([
    { id: 'support',     label: 'open support threads', count: supportOpenCount,    tone: 'urgent',  onClick: () => setActiveTab('support')   },
    { id: 'reports',     label: 'open venue reports',   count: reportsOpenCount,    tone: 'urgent',  onClick: () => setActiveTab('reports')   },
    { id: 'disputes',    label: 'open disputes',        count: disputesOpenCount,   tone: 'urgent',  onClick: () => setActiveTab('disputes')  },
    { id: 'venues-hold', label: 'venues on admin hold', count: venuesOnHoldCount,   tone: 'hold',    onClick: () => setActiveTab('venue-apps') },
    { id: 'venue-apps',  label: 'venue applications',  count: pendingApprovalCount, tone: 'pending', onClick: () => setActiveTab('approving') },
    { id: 'experiences', label: 'experiences to review', count: experiencesPendingCount, tone: 'pending', onClick: () => setActiveTab('experiences') },
  ]), [pendingApprovalCount, venuesOnHoldCount, supportOpenCount, reportsOpenCount, disputesOpenCount, experiencesPendingCount]);

  // ── Action functions ──────────────────────────────────────────────────────
  const toggleMobileView = () => {
    const next = !isMobileView;
    setIsMobileView(next);
    try { localStorage.setItem('adminMobileView', String(next)); } catch {}
  };

  const navigateToApp = () => navigate('/guest/home');

  const handleLogout = () => {
    localStorage.clear();
    if (onLogout) onLogout();
    navigate('/login', { replace: true });
  };

  const dismissAttention = () => {
    setAttentionDismissed(true);
    try { sessionStorage.setItem('adminAttentionDismissed', '1'); } catch {}
  };

  const approveApp = async (appId) => {
    try {
      await axios.put(`${API}/admin/host-applications/${appId}/approve`, {}, { headers });
      toast.success('Application approved!');
      fetchData(activeTab);
      refreshPendingApprovals();
    } catch (err) { toast.error('Failed'); }
  };

  const rejectApp = async (appId) => {
    try {
      await axios.put(`${API}/admin/host-applications/${appId}/reject`, {}, { headers });
      toast.success('Application rejected');
      fetchData(activeTab);
      refreshPendingApprovals();
    } catch (err) { toast.error('Failed'); }
  };

  const approveVenueApplication = async (appId) => {
    try {
      await axios.put(`${API}/admin/venue-applications/${appId}/approve`, {}, { headers });
      toast.success('Venue approved — check the Venues tab (On hold until payout connects)');
      setSelectedVenueApp(null);
      setActiveTab('venue-apps', { replace: false });
      refreshPendingApprovals();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to approve venue'); }
  };

  const requestVenueMediaChanges = async (appId, payload) => {
    try {
      const res = await axios.put(`${API}/admin/venue-applications/${appId}/request-media-changes`, payload, { headers });
      const removed = res.data?.removed_photo_count || 0;
      const videoRemoved = res.data?.video_removed;
      const fieldCount = res.data?.field_issue_count || 0;
      const parts = [];
      if (removed) parts.push(`${removed} photo${removed === 1 ? '' : 's'}`);
      if (videoRemoved) parts.push('intro video');
      if (fieldCount) parts.push(`${fieldCount} info fix${fieldCount === 1 ? '' : 'es'}`);
      toast.success(`Revision sent · ${parts.join(' · ') || 'updates flagged'} · owner notified`);
      setSelectedVenueApp(null);
      fetchData(activeTab);
      refreshPendingApprovals();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send revision request');
      throw err;
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers });
      toast.success('User deleted');
      fetchData('users');
    } catch (err) { toast.error('Failed'); }
  };

  const approveReferral = async (refId) => {
    try {
      await axios.put(`${API}/admin/referrals/${refId}/approve`, {}, { headers });
      toast.success('Referral approved');
      fetchData('approving');
    } catch (err) { toast.error('Failed'); }
  };

  const rejectReferral = async (refId) => {
    try {
      await axios.put(`${API}/admin/referrals/${refId}/reject`, {}, { headers });
      toast.success('Referral declined');
      fetchData('approving');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to decline referral'); }
  };

  const removeReferral = async (refId) => {
    try {
      await axios.delete(`${API}/admin/referrals/${refId}`, { headers });
      toast.success('Referral removed');
      fetchData('approving');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to remove referral'); }
  };

  const markReferralInviteSent = async (refId) => {
    try {
      await axios.put(`${API}/admin/referrals/${refId}/invite-sent`, {}, { headers });
      fetchData('approving');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to mark invite as sent'); }
  };

  const generateHostPayoutBatch = async () => {
    try {
      await axios.post(`${API}/admin/host-payouts/generate-batch`, { month: financeMonth }, { headers });
      toast.success('Host payout batch generated');
      fetchData('transactions');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to generate payout batch'); }
  };

  const markHostPayoutPaid = async (batchId, hostId) => {
    try {
      await axios.post(`${API}/admin/host-payouts/batches/${batchId}/hosts/${hostId}/mark-paid`, {}, { headers });
      toast.success('Host payout marked as paid');
      fetchData('transactions');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to mark host payout as paid'); }
  };

  const runHostPayoutSchedulerNow = async () => {
    try {
      const res = await axios.post(`${API}/admin/host-payouts/run-scheduler-now`, {}, { headers });
      if (res.data?.status === 'failed') {
        toast.error(res.data?.error_message || 'Payout run failed');
      } else {
        const s = res.data?.summary || {};
        toast.success(`Payout run completed — ${s.executed || 0} paid, ${s.skipped || 0} skipped, ${s.failed || 0} failed`);
      }
      fetchData('transactions');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to run payout scheduler'); }
  };

  const runConnectRemindersNow = async () => {
    try {
      const res = await axios.post(`${API}/admin/host-payouts/run-connect-reminders-now`, {}, { headers });
      const s = res.data?.summary || {};
      toast.success(`Connect reminders sent: ${s.notified || 0} (checked ${s.checked || 0} hosts)`);
      fetchData('transactions');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send connect reminders'); }
  };

  const exportHostPayoutCsv = async () => {
    try {
      const response = await axios.get(`${API}/admin/host-payouts/export.csv`, { headers, params: { month: financeMonth }, responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `allure-host-payouts-${financeMonth}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success('CSV export downloaded');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to export CSV'); }
  };

  const exportHostPayoutUploadCsv = async () => {
    try {
      const response = await axios.get(`${API}/admin/host-payouts/export-upload.csv`, { headers, params: { month: financeMonth }, responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `allure-host-payout-upload-${financeMonth}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success('Upload CSV downloaded');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to export upload CSV'); }
  };

  const fetchGuestDetail = async (guestId) => {
    setGuestDetailLoading(true);
    setSelectedHost(null);
    setHostDetail(null);
    try {
      const res = await axios.get(`${API}/admin/guests/${guestId}/detail`, { headers });
      setGuestDetail(res.data);
      setSelectedGuest(guestId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load guest details');
    } finally { setGuestDetailLoading(false); }
  };

  const fetchHostDetail = async (hostId) => {
    setHostDetailLoading(true);
    setSelectedGuest(null);
    setGuestDetail(null);
    try {
      const res = await axios.get(`${API}/admin/hosts/${hostId}/detail`, { headers });
      setHostDetail(res.data);
      setSelectedHost(hostId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load host details');
    } finally { setHostDetailLoading(false); }
  };

  const startAdminConversation = async (recipientId, chatName) => {
    try {
      const res = await axios.post(`${API}/messages/open-conversation`, { recipient_id: recipientId }, { headers });
      navigate(`/admin/chat/${res.data.id}`, { state: { chatName } });
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to start conversation'); }
  };

  const openSupportBubbleForUser = async (userId) => {
    try {
      setSelectedSupportThread(null);
      setSelectedSupportRequestId(null);
      setSupportDraft('');
      const res = await axios.post(`${API}/admin/support/open-thread`, { user_id: userId }, { headers });
      setSelectedSupportThread(res.data);
      setSelectedSupportRequestId(res.data.id);
      setFloatingSupportVisible(true);
      setFloatingSupportMinimized(false);
      fetchData('support');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to open support thread'); }
  };

  // NEW (Feb 2026): open a support ticket in the regular messenger.
  // The user's original ticket message is back-filled as the first
  // messenger message and the admin lands on `/admin/chat/:convoId`
  // where they can reply through the normal chat flow and hit
  // "Mark solved" when the case is done.
  const openTicketInMessenger = async (ticketId, chatName = 'Support') => {
    try {
      const res = await axios.post(
        `${API}/admin/support/requests/${ticketId}/open-in-messenger`,
        {},
        { headers },
      );
      const convoId = res.data?.conversation_id;
      if (!convoId) throw new Error('Missing conversation_id');
      navigate(`/admin/chat/${convoId}`, { state: { chatName } });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to open messenger conversation');
    }
  };

  const removeGuestQr = async (passId) => {
    if (!window.confirm('Remove this active QR code?')) return;
    try {
      await axios.delete(`${API}/admin/guest-passes/${passId}`, { headers });
      toast.success('Guest QR removed');
      if (selectedGuest) fetchGuestDetail(selectedGuest);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to remove QR'); }
  };

  const removeGuestReview = async (reviewId) => {
    if (!window.confirm('Delete this guest review?')) return;
    try {
      await axios.delete(`${API}/admin/reviews/${reviewId}`, { headers });
      toast.success('Review removed');
      if (selectedGuest) fetchGuestDetail(selectedGuest);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to remove review'); }
  };

  const sendSupportReply = async () => {
    if (!selectedSupportRequestId || !supportDraft.trim()) return;
    try {
      await axios.post(`${API}/support/requests/${selectedSupportRequestId}/messages`, { content: supportDraft.trim() }, { headers });
      setSupportDraft('');
      fetchSupportThread(selectedSupportRequestId);
      fetchData('support');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send support reply'); }
  };

  const updateSupportStatus = async (requestId, status) => {
    try {
      await axios.put(`${API}/admin/support/requests/${requestId}/status`, { status }, { headers });
      fetchData('support');
      fetchSupportThread(requestId);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update support status'); }
  };

  const saveRecentSearch = (value) => {
    const normalized = value.trim();
    if (!normalized) return;
    const next = [normalized, ...recentSearches.filter((e) => e !== normalized)].slice(0, 6);
    setRecentSearches(next);
    localStorage.setItem('adminRecentSearches', JSON.stringify(next));
  };

  const deleteListingRecord = async (appId) => {
    if (!window.confirm('Delete this listing record?')) return;
    try {
      await axios.delete(`${API}/admin/host-applications/${appId}`, { headers });
      toast.success('Listing deleted');
      fetchData('applications');
      refreshPendingApprovals();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete listing'); }
  };

  const openGeneralMapForItem = (targetId, filters) => {
    setSelectedVenueApp(null);
    setSelectedHost(null);
    setSelectedGuest(null);
    setGlobalMapFilters(filters?.length ? filters : ['all']);
    setSelectedMapId(targetId);
    setActiveTab('maps');
  };

  const openMapItemDetail = (item) => {
    if (!item) return;
    if (item.entityType === 'venue') {
      const venueAppId = String(item.id || '').replace(/^venue-/, '');
      const target = venueApps.find((e) => e.id === venueAppId);
      if (target) { setSelectedVenueApp({ ...target }); setEditMode(false); setEditData({}); setActiveTab('venue-apps'); return; }
    }
    if (item.entityType === 'listing') {
      const appId = String(item.id || '').replace(/^listing-/, '');
      const application = applications.find((e) => e.id === appId);
      if (application?.user?.id) { setActiveTab('applications'); fetchHostDetail(application.user.id); return; }
    }
    toast.message('Detail view not available for this item yet.');
  };

  const venueAction = async (appId, action, payload = null) => {
    try {
      const body = payload || {};
      const res = await axios.put(`${API}/admin/venue-applications/${appId}/${action}`, body, { headers });
      const msg =
        action === 'go-live'  ? 'Venue is now live on the guest map!' :
        action === 'hold'     ? 'Venue placed on hold — admin will be reminded in 5 days' :
        action === 'unhold'   ? (res.data?.status === 'live' ? 'Hold lifted — venue is back on the map' : 'Hold lifted — waiting on payout') :
        action === 'approve'  ? 'Approved — owner will connect payout, then auto-flips live' :
        action === 'reject'   ? 'Venue rejected' : 'Done';
      toast.success(msg);
      setSelectedVenueApp(null);
      fetchData(activeTab === 'approving' ? 'approving' : 'venue-apps');
      refreshPendingApprovals();
    } catch (err) { toast.error('Failed: ' + (err.response?.data?.detail || err.message)); }
  };

  const saveVenueEdit = async (appId) => {
    try {
      const payload = { ...editData, main_photo: editData.photo_urls?.[0] || '' };
      await axios.put(`${API}/admin/venue-applications/${appId}/edit`, payload, { headers });
      toast.success('Venue listing updated');
      setEditMode(false);
      const res = await axios.get(`${API}/admin/venue-applications`, { headers });
      setVenueApps(res.data);
      const updated = res.data.find((a) => a.id === appId);
      if (updated) setSelectedVenueApp(updated);
    } catch (err) { toast.error('Failed to save'); }
  };

  const movePhoto = (fromIdx, toIdx) => {
    if (toIdx < 0 || toIdx >= (editData.photo_urls || []).length) return;
    const photos = [...editData.photo_urls];
    const [moved] = photos.splice(fromIdx, 1);
    photos.splice(toIdx, 0, moved);
    setEditData({ ...editData, photo_urls: photos, main_photo: photos[0] || '' });
  };

  const handleFilterToggle = (filterId) =>
    setGlobalMapFilters((current) => toggleSelectableFilters(current, filterId));

  const toggleRadius = () => setRadiusEnabled((current) => !current);

  const onViewReviews = (payload) => {
    if (!payload?.venueId) return;
    setReviewsModalVenue({ id: payload.venueId, name: payload.venueName || '' });
  };

  // ── Return ─────────────────────────────────────────────────────────────────
  return {
    // State
    activeTab, setActiveTab,
    applications, users, venues, ignoredIcalEvents,
    financeOverview, financeMonth, setFinanceMonth,
    referrals, venueApps, setVenueApps,
    selectedVenueApp, setSelectedVenueApp,
    selectedGuest, setSelectedGuest,
    selectedHost, setSelectedHost,
    guestDetail, setGuestDetail,
    hostDetail, setHostDetail,
    guestDetailLoading, hostDetailLoading,
    editMode, setEditMode,
    editData, setEditData,
    loading,
    searchQuery, setSearchQuery,
    recentSearches,
    supportRequests,
    supportFilter, setSupportFilter,
    faqStats,
    selectedSupportRequestId, setSelectedSupportRequestId,
    selectedSupportThread,
    supportDraft, setSupportDraft,
    supportLoading,
    pendingApprovalCount, unavailableListingCount, venuesOnHoldCount,
    supportOpenCount, reportsOpenCount, disputesOpenCount,
    experiencesPendingCount, setExperiencesPendingCount,
    attentionDismissed,
    expandedTimelineId, setExpandedTimelineId,
    listingStatusFilter, setListingStatusFilter,
    globalMapFilters, setGlobalMapFilters, selectedMapId, setSelectedMapId, radiusEnabled,
    floatingSupportVisible, setFloatingSupportVisible,
    floatingSupportMinimized, setFloatingSupportMinimized,
    reviewsModalVenue, setReviewsModalVenue,
    resetModalOpen, setResetModalOpen,
    currentUser, isMobileView,
    // Refs
    floatingSupportBottomRef,
    // Derived
    headers, user, isMainAdmin,
    searchResults, pendingVenueApps, revisionVenueApps,
    globalMapItems, tabCounts, attentionItems,
    // Actions
    toggleMobileView, navigateToApp, handleLogout, dismissAttention,
    approveApp, rejectApp, approveVenueApplication,
    requestVenueMediaChanges, deleteUser,
    approveReferral, rejectReferral, removeReferral, markReferralInviteSent,
    generateHostPayoutBatch, markHostPayoutPaid,
    runHostPayoutSchedulerNow, runConnectRemindersNow,
    exportHostPayoutCsv, exportHostPayoutUploadCsv,
    fetchGuestDetail, fetchHostDetail,
    startAdminConversation, openSupportBubbleForUser, openTicketInMessenger,
    removeGuestQr, removeGuestReview,
    fetchSupportThread, sendSupportReply, updateSupportStatus,
    saveRecentSearch, deleteListingRecord,
    openGeneralMapForItem, openMapItemDetail,
    venueAction, saveVenueEdit, movePhoto,
    handleFilterToggle, toggleRadius, onViewReviews,
  };
};
